import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getHeadsOfAccounts, destroyHeadOfAccount } from '../../../api/HeadOfAccountsAPI';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import PerPageBlock from '../../../components/common/PerPageBlock';
import AppToaster from '../../AppToaster';

const HeadOfAccountsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [headsList, setHeadsList] = useState({ data: [], last_page: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [triggerFetch, setTriggerFetch] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});

    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success!', message: successMessage });
            navigate(location.pathname, { replace: true });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        setIsLoading(true);
        const filters = { page: currentPage, per_page: perPage, search_term: searchTermCompleted };
        getHeadsOfAccounts(filters, (response) => {
            setIsLoading(false);
            if (response.heads_of_accounts) {
                setHeadsList(response.heads_of_accounts);
            }
        });
    }, [currentPage, perPage, searchTermCompleted, triggerFetch]);

    const handleAddClick = () => navigate('/club_portal/head-of-accounts/create');
    const handleEditClick = (id) => navigate(`/club_portal/head-of-accounts/edit/${id}`);

    const handleDeleteClick = (head) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `<p>Delete account head: <strong>${head.name}</strong>?</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                destroyHeadOfAccount(head, (response) => {
                    if (response.success) {
                        Swal.fire('Deleted!', 'The record has been deleted.', 'success');
                        setTriggerFetch(p => !p);
                    } else {
                        Swal.fire('Error!', 'Could not delete the record.', 'error');
                    }
                });
            }
        });
    };

    return (
        <main>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <div className="container-fluid">
                <AppBreadCrumb title="Heads of Accounts" />
                <div className="row">
                    <div className="col-12">
                        <div className="tab-wrapper mb-3">
                            <ul className="tabs">
                                <li className="tab-link active">All Accounts</li>
                                <li className="ms-md-auto">
                                    <div className="text-md-end d-flex flex-column flex-md-row align-items-md-center">
                                        <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                        <button className="text-nowrap btn btn-primary btn-sm mt-2 mt-md-0 ms-md-3" onClick={handleAddClick}>
                                            <i className="ti ti-plus f-s-18"></i> Add Head
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div className="content-wrapper">
                            <div className="d-flex m-md-3">
                                {headsList.data.length > 0 && <AppPaginator totalPages={headsList.last_page} currentPage={currentPage} setCurrentPage={setCurrentPage} />}
                                {isLoading && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                            </div>
                            <div className="tabs-content active">
                                <div className="row">
                                    {!isLoading && (
                                        headsList.data.length > 0 ? headsList.data.map(head => (
                                            <div className="col-md-6 col-xl-4 project-card" key={head.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            <div className={`h-40 w-40 d-flex-center b-r-50 overflow-hidden bg-light-${head.account_type === 'income' ? 'success' : 'danger'} text-${head.account_type === 'income' ? 'success' : 'danger'}`}>
                                                                <i className={`fas fa-arrow-${head.account_type === 'income' ? 'down' : 'up'} fa-lg`}></i>
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600 txt-ellipsis-1">{head.name}</h6>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button className="bg-none border-0" data-bs-toggle="dropdown"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(head.id)}><i className="ti ti-edit text-success" /> Edit</a></li>
                                                                    <li><a className="dropdown-item" onClick={() => handleDeleteClick(head)}><i className="ti ti-trash text-danger" /> Delete</a></li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )) : (
                                            <div className="card mb-3"><div className="row g-0"><div className="col-md-8"><div className="card-body"><h5 className="card-title">No Heads of Accounts Found</h5><p className="card-text">Click "Add Head" to create categories for your income and expenses.</p></div></div><div className="col-md-4"><img className="img-fluid" src="/assets/images/blog-app/09.jpg" /></div></div></div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default HeadOfAccountsPage;
